import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import { tokens } from "../theme";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SecurityIcon from "@mui/icons-material/Security";

export default function PermissionMatrix({ type, permissions, onChange }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('permissionGroups');

  const permissionStructure = useMemo(() => ({
    admin: {
      files: { label: "Files", operations: ['create', 'update', 'view', 'delete'] },
      forms: { label: "Forms", operations: ['create', 'update', 'view', 'delete'] },
      patients: { label: "Patients", operations: ['create', 'update', 'view', 'delete'] },
      doctors: { label: "Doctors", operations: ['create', 'update', 'delete'] },
      form_answers: { label: "Form Answers", operations: ['delete'] },
    },
    doctor: {
      own_doctors: { label: t('ownDoctors'), operations: ['create', 'update', 'delete'] },
      own_patients: { label: t('ownPatients'), operations: ['create', 'update', 'view', 'delete'] },
      own_form_answers: { label: t('ownFormAnswers'), operations: ['delete'] },
      own_files: { label: t('ownFiles'), operations: ['create', 'update', 'view', 'delete'] },
      own_forms: { label: t('ownForms'), operations: ['create', 'update', 'view', 'delete'] },
      own_invitations: { label: t('ownInvitations'), operations: ['create', 'update', 'view', 'delete'] },
      own_permission_groups: { label: t('ownPermissionGroups'), operations: ['create', 'update', 'add', 'remove', 'view'] },
    }
  }), [t]);

  const structure = permissionStructure[type] || permissionStructure.admin;

  const handlePermissionChange = (resource, operation) => {
    // Special handling for permission_groups: 'add' -> 'can_own_permission_groups_add', 'remove' -> 'can_own_permission_groups_remove'
    let permissionKey;
    if (resource === 'own_permission_groups') {
      if (operation === 'add') {
        permissionKey = 'can_own_permission_groups_add';
      } else if (operation === 'remove') {
        permissionKey = 'can_own_permission_groups_remove';
      } else if (operation === 'update') {
        permissionKey = 'can_own_permission_groups_update';
      } else {
        permissionKey = `can_${resource}_${operation}`;
      }
    } else {
      permissionKey = `can_${resource}_${operation}`;
    }
    
    onChange({
      ...permissions,
      [permissionKey]: !permissions[permissionKey],
    });
  };

  const handleSelectAll = (resource, value) => {
    const resourceData = structure[resource];
    const newPermissions = { ...permissions };
    
    resourceData.operations.forEach(operation => {
      // Special handling for permission_groups: 'add' -> 'can_own_permission_groups_add', 'remove' -> 'can_own_permission_groups_remove'
      let permissionKey;
      if (resource === 'own_permission_groups') {
        if (operation === 'add') {
          permissionKey = 'can_own_permission_groups_add';
        } else if (operation === 'remove') {
          permissionKey = 'can_own_permission_groups_remove';
        } else if (operation === 'update') {
          permissionKey = 'can_own_permission_groups_update';
        } else {
          permissionKey = `can_${resource}_${operation}`;
        }
      } else {
        permissionKey = `can_${resource}_${operation}`;
      }
      newPermissions[permissionKey] = value;
    });
    
    onChange(newPermissions);
  };

  const isResourceFullySelected = (resource) => {
    const resourceData = structure[resource];
    return resourceData.operations.every(operation => {
      // Special handling for permission_groups: 'add' -> 'can_own_permission_groups_add', 'remove' -> 'can_own_permission_groups_remove'
      let permissionKey;
      if (resource === 'own_permission_groups') {
        if (operation === 'add') {
          permissionKey = 'can_own_permission_groups_add';
        } else if (operation === 'remove') {
          permissionKey = 'can_own_permission_groups_remove';
        } else if (operation === 'update') {
          permissionKey = 'can_own_permission_groups_update';
        } else {
          permissionKey = `can_${resource}_${operation}`;
        }
      } else {
        permissionKey = `can_${resource}_${operation}`;
      }
      return permissions[permissionKey] === true;
    });
  };

  const operationColors = {
    create: colors.greenAccent[500],
    update: colors.blueAccent[500],
    view: colors.grey[500],
    delete: colors.redAccent[500],
    upload: colors.greenAccent[600],
    add: colors.greenAccent[600],
    remove: colors.orangeAccent?.[500] || colors.redAccent[400],
  };

  const operationIcons = {
    create: '➕',
    update: '✏️',
    view: '👁️',
    delete: '🗑️',
    upload: '📤',
    add: '➕',
    remove: '➖',
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
        border: `1px solid ${colors.grey[800]}30`,
        borderRadius: "16px",
        overflow: "hidden",
      }}
    >
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: `${colors.primary[600]}80`,
              borderBottom: `2px solid ${colors.grey[800]}30`,
            }}
          >
            <TableCell
              sx={{
                color: colors.grey[100],
                fontWeight: 700,
                fontSize: "0.95rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                width: "250px",
              }}
            >
              {t('resource')}
            </TableCell>
            <TableCell
              align="center"
              sx={{
                color: colors.grey[100],
                fontWeight: 700,
                fontSize: "0.95rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {t('permissions')}
            </TableCell>
            <TableCell
              align="center"
              sx={{
                color: colors.grey[100],
                fontWeight: 700,
                fontSize: "0.95rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                width: "120px",
              }}
            >
              {t('selectAll')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(structure).map(([resource, resourceData]) => {
            const isFullySelected = isResourceFullySelected(resource);
            
            return (
              <TableRow
                key={resource}
                sx={{
                  "&:hover": {
                    backgroundColor: `${colors.primary[600]}30`,
                  },
                  borderBottom: `1px solid ${colors.grey[800]}20`,
                }}
              >
                {/* Resource Name */}
                <TableCell
                  sx={{
                    color: colors.grey[200],
                    fontSize: "0.9rem",
                    fontWeight: 600,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SecurityIcon sx={{ fontSize: 20, color: colors.blueAccent[400] }} />
                    {resourceData.label}
                  </Box>
                </TableCell>

                {/* Permission Checkboxes */}
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                    {resourceData.operations.map((operation) => {
                      // Special handling for permission_groups: 'add' -> 'can_own_permission_groups_add', 'remove' -> 'can_own_permission_groups_remove'
                      let permissionKey;
                      if (resource === 'own_permission_groups') {
                        if (operation === 'add') {
                          permissionKey = 'can_own_permission_groups_add';
                        } else if (operation === 'remove') {
                          permissionKey = 'can_own_permission_groups_remove';
                        } else {
                          permissionKey = `can_${resource}_${operation}`;
                        }
                      } else {
                        permissionKey = `can_${resource}_${operation}`;
                      }
                      const isChecked = permissions[permissionKey] === true;
                      
                      return (
                        <Tooltip
                          key={operation}
                          title={`${t(operation)} ${resourceData.label}`}
                        >
                          <Box
                            onClick={() => handlePermissionChange(resource, operation)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "8px",
                              cursor: "pointer",
                              backgroundColor: isChecked
                                ? `${operationColors[operation]}30`
                                : `${colors.grey[800]}20`,
                              border: `1px solid ${isChecked ? operationColors[operation] : colors.grey[700]}40`,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: isChecked
                                  ? `${operationColors[operation]}50`
                                  : `${colors.grey[700]}40`,
                                transform: "scale(1.05)",
                              },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                color: isChecked ? colors.grey[100] : colors.grey[400],
                              }}
                            >
                              {operationIcons[operation]} {t(operation)}
                            </Typography>
                            {isChecked && (
                              <CheckCircleIcon
                                sx={{
                                  fontSize: 14,
                                  color: operationColors[operation],
                                }}
                              />
                            )}
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </TableCell>

                {/* Select All for Resource */}
                <TableCell align="center">
                  <Tooltip title={isFullySelected ? t('deselectAll') : t('selectAll')}>
                    <IconButton
                      size="small"
                      onClick={() => handleSelectAll(resource, !isFullySelected)}
                      sx={{
                        color: isFullySelected ? colors.greenAccent[400] : colors.grey[500],
                        backgroundColor: isFullySelected
                          ? `${colors.greenAccent[800]}30`
                          : `${colors.grey[800]}20`,
                        border: `1px solid ${isFullySelected ? colors.greenAccent[500] : colors.grey[700]}40`,
                        "&:hover": {
                          backgroundColor: isFullySelected
                            ? `${colors.greenAccent[700]}50`
                            : `${colors.grey[700]}40`,
                        },
                      }}
                    >
                      {isFullySelected ? (
                        <CheckCircleIcon fontSize="small" />
                      ) : (
                        <CancelIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

